import React, { useState, useEffect } from 'react';
import s from './Settings.module.css';
import { settingsApi } from '../../../api/settings';
import { WordEditor } from '../NewsEdit/WordEditor';

interface Contact {
    id: number;
    phone: string;
    name: string;
}

interface SettingsData {
    address: string;
    email: string;
    contacts: Contact[];
    privacyPolicy: string;
    consentData: string;
}

export const Settings: React.FC = () => {
    const [settings, setSettings] = useState<SettingsData>({
        address: '',
        email: '',
        contacts: [{ id: 1, phone: '', name: '' }],
        privacyPolicy: '<p></p>',
        consentData: '<p></p>',
    });

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingContent, setEditingContent] = useState<'privacy' | 'consent' | null>(null);
    const [tempContent, setTempContent] = useState('');

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const data = await settingsApi.getAllAdmin();

            const settingsMap: any = {};
            data.forEach(item => {
                settingsMap[item.key] = item.value;
            });

            let contacts: Contact[] = [{ id: 1, phone: '', name: '' }];
            if (settingsMap.contacts) {
                try {
                    contacts = JSON.parse(settingsMap.contacts);
                } catch {
                    contacts = [{ id: 1, phone: '', name: '' }];
                }
            }

            let privacyPolicy = '<p></p>';
            if (settingsMap.privacy_policy_text) {
                try {
                    const parsed = JSON.parse(settingsMap.privacy_policy_text);
                    if (Array.isArray(parsed)) {

                        privacyPolicy = parsed.map((block: any) => {
                            if (block.type === 'paragraph') {
                                return `<p>${block.content || ''}</p>`;
                            }
                            return '';
                        }).join('');
                    } else {
                        privacyPolicy = settingsMap.privacy_policy_text;
                    }
                } catch {
                    privacyPolicy = settingsMap.privacy_policy_text;
                }
            }

            let consentData = '<p></p>';
            if (settingsMap.consent_data_text) {
                try {
                    const parsed = JSON.parse(settingsMap.consent_data_text);
                    if (Array.isArray(parsed)) {
                        consentData = parsed.map((block: any) => {
                            if (block.type === 'paragraph') {
                                return `<p>${block.content || ''}</p>`;
                            }
                            return '';
                        }).join('');
                    } else {
                        consentData = settingsMap.consent_data_text;
                    }
                } catch {
                    consentData = settingsMap.consent_data_text;
                }
            }

            setSettings({
                address: settingsMap.office_address || '',
                email: settingsMap.contact_email || '',
                contacts: contacts,
                privacyPolicy: privacyPolicy || '<p></p>',
                consentData: consentData || '<p></p>',
            });
        } catch (error) {
            console.error('Ошибка загрузки настроек:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSettings({
            ...settings,
            [e.target.name]: e.target.value
        });
    };

    const handleContactChange = (index: number, field: 'phone' | 'name', value: string) => {
        const newContacts = [...settings.contacts];
        newContacts[index][field] = value;
        setSettings({
            ...settings,
            contacts: newContacts
        });
    };

    const addContact = () => {
        const newId = Math.max(0, ...settings.contacts.map(c => c.id)) + 1;
        setSettings({
            ...settings,
            contacts: [...settings.contacts, { id: newId, phone: '', name: '' }]
        });
    };

    const removeContact = (index: number) => {
        if (settings.contacts.length > 1) {
            const newContacts = settings.contacts.filter((_, i) => i !== index);
            setSettings({
                ...settings,
                contacts: newContacts
            });
        }
    };

    const openEditor = (type: 'privacy' | 'consent') => {
        setEditingContent(type);
        setTempContent(type === 'privacy' ? settings.privacyPolicy : settings.consentData);
    };

    const closeEditor = () => {
        setEditingContent(null);
        setTempContent('');
    };

    const saveContent = async () => {
        if (!editingContent) return;

        const key = editingContent === 'privacy' ? 'privacy_policy_text' : 'consent_data_text';
        const description = editingContent === 'privacy'
            ? 'Политика конфиденциальности'
            : 'Согласие на обработку персональных данных';

        try {
            await settingsApi.update(key, {
                value: tempContent,
                description
            });

            if (editingContent === 'privacy') {
                setSettings({
                    ...settings,
                    privacyPolicy: tempContent
                });
            } else {
                setSettings({
                    ...settings,
                    consentData: tempContent
                });
            }

            closeEditor();
        } catch (error) {
            console.error('Ошибка сохранения:', error);
            alert('Ошибка при сохранении');
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const promises = [];

            promises.push(settingsApi.update('office_address', { value: settings.address, description: 'Адрес офиса' }));
            promises.push(settingsApi.update('contact_email', { value: settings.email, description: 'Email' }));
            promises.push(settingsApi.update('contacts', { value: JSON.stringify(settings.contacts), description: 'Контакты' }));
            promises.push(settingsApi.update('privacy_policy_text', { value: settings.privacyPolicy, description: 'Политика конфиденциальности' }));
            promises.push(settingsApi.update('consent_data_text', { value: settings.consentData, description: 'Согласие на обработку персональных данных' }));

            await Promise.all(promises);
            console.log('✅ Настройки сохранены');
            setIsEditing(false);
        } catch (error) {
            console.error('Ошибка сохранения настроек:', error);
            alert('Ошибка при сохранении настроек');
        } finally {
            setSaving(false);
        }
    };

    const renderContentPreview = (html: string) => {
        if (!html || html === '<p></p>') {
            return <p className={s.emptyPreview}>Нет содержимого</p>;
        }
        return <div dangerouslySetInnerHTML={{ __html: html }} />;
    };

    if (loading) {
        return <div className={s.loading}>Загрузка...</div>;
    }

    return (
        <div className={s.settings}>
            <div className={s.header}>
                <h1 className={s.title}>Настройки</h1>
                {!isEditing ? (
                    <button className={s.editButton} onClick={() => setIsEditing(true)}>
                        Редактировать
                    </button>
                ) : (
                    <div className={s.actionButtons}>
                        <button className={s.saveButton} onClick={handleSave} disabled={saving}>
                            {saving ? 'Сохранение...' : 'Сохранить'}
                        </button>
                        <button className={s.cancelButton} onClick={() => setIsEditing(false)}>
                            Отмена
                        </button>
                    </div>
                )}
            </div>

            <div className={s.settingsGrid}>
                <div className={s.settingsCard}>
                    <h2 className={s.cardTitle}>Контакты</h2>
                    <div className={s.formGroup}>
                        <label className={s.label}>Email</label>
                        <input
                            type="email"
                            name="email"
                            value={settings.email}
                            onChange={handleChange}
                            disabled={!isEditing}
                            className={`${s.input} ${!isEditing ? s.disabled : ''}`}
                        />
                    </div>
                    <div className={s.formGroup}>
                        <label className={s.label}>Телефоны</label>
                        {settings.contacts.map((contact, index) => (
                            <div key={contact.id} className={s.contactRow}>
                                <input
                                    type="text"
                                    placeholder="Номер телефона"
                                    value={contact.phone}
                                    onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                                    disabled={!isEditing}
                                    className={`${s.input} ${!isEditing ? s.disabled : ''}`}
                                />
                                <input
                                    type="text"
                                    placeholder="ФИО"
                                    value={contact.name}
                                    onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                                    disabled={!isEditing}
                                    className={`${s.input} ${!isEditing ? s.disabled : ''}`}
                                />
                                {isEditing && settings.contacts.length > 1 && (
                                    <button
                                        className={s.removeContactBtn}
                                        onClick={() => removeContact(index)}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        ))}
                        {isEditing && (
                            <button className={s.addContactBtn} onClick={addContact}>
                                + Добавить контакт
                            </button>
                        )}
                    </div>
                    <div className={s.formGroup}>
                        <label className={s.label}>Адрес офиса</label>
                        <input
                            type="text"
                            name="address"
                            value={settings.address}
                            onChange={handleChange}
                            disabled={!isEditing}
                            className={`${s.input} ${!isEditing ? s.disabled : ''}`}
                        />
                    </div>
                </div>

                <div className={s.settingsCard}>
                    <h2 className={s.cardTitle}>Правовые документы</h2>
                    <div className={s.formGroup}>
                        <label className={s.label}>Политика конфиденциальности</label>
                        <div className={s.contentPreview}>
                            {renderContentPreview(settings.privacyPolicy)}
                        </div>
                        {isEditing && (
                            <button
                                className={s.editContentBtn}
                                onClick={() => openEditor('privacy')}
                            >
                                Редактировать
                            </button>
                        )}
                    </div>
                    <div className={s.formGroup}>
                        <label className={s.label}>Согласие на обработку персональных данных</label>
                        <div className={s.contentPreview}>
                            {renderContentPreview(settings.consentData)}
                        </div>
                        {isEditing && (
                            <button
                                className={s.editContentBtn}
                                onClick={() => openEditor('consent')}
                            >
                                Редактировать
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {editingContent && (
                <div className={s.modalOverlay} onClick={closeEditor}>
                    <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={s.modalHeader}>
                            <h2>
                                {editingContent === 'privacy'
                                    ? 'Редактирование политики конфиденциальности'
                                    : 'Редактирование согласия на обработку персональных данных'}
                            </h2>
                            <button className={s.modalCloseBtn} onClick={closeEditor}>✕</button>
                        </div>

                        <div className={s.editorWrapper}>
                            <WordEditor
                                content={tempContent}
                                onChange={setTempContent}
                                placeholder="Введите текст..."
                            />
                        </div>

                        <div className={s.modalActions}>
                            <button className={s.saveContentBtn} onClick={saveContent}>
                                Сохранить
                            </button>
                            <button className={s.cancelContentBtn} onClick={closeEditor}>
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};